import {
  IconArrowsExchange,
  IconEye,
  IconLock,
  IconSearch,
  IconShoppingCart,
  IconSparkles,
  IconTargetArrow,
  IconUsers,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

export const Route = createFileRoute("/_authed/amc/audiences")({
  component: AmcAudienceHubPage,
});

const AUDIENCE_TEMPLATES = [
  { key: "recentPurchasers", icon: IconShoppingCart },
  { key: "highValue", icon: IconSparkles, badgeKey: "badge" },
  { key: "cartAbandoners", icon: IconShoppingCart },
  { key: "dpvVisitors", icon: IconEye, badgeKey: "badge" },
  { key: "saContentSearch", icon: IconSearch, noteKey: "note" },
  { key: "adExposure", icon: IconTargetArrow },
  { key: "cartLookalike", icon: IconArrowsExchange },
] as const;

export function AmcAudienceHubPage(): React.ReactElement {
  const { t } = useTranslation("amc");

  return (
    <main className="flex min-h-full min-w-[1024px] flex-col gap-4 px-6 pb-6 pt-5">
      <Card className="shrink-0 gap-5 py-5 shadow-[0_1px_3px_rgb(10_10_10_/_0.03)]">
        <CardHeader className="px-5">
          <CardTitle className="text-[18px] font-semibold tracking-[-0.02em] text-ink">
            {t("audiences.templates.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5">
          <div className="grid grid-cols-4 gap-3">
            {AUDIENCE_TEMPLATES.map((template) => {
              const Icon = template.icon;
              const translationKey = `audiences.templates.${template.key}`;

              return (
                <button
                  key={template.key}
                  type="button"
                  disabled
                  aria-label={t(`${translationKey}.title`)}
                  className="flex min-h-[160px] cursor-not-allowed flex-col rounded-[12px] border border-line bg-surface p-4 text-left opacity-55"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="flex size-8 items-center justify-center rounded-[8px] bg-subtle text-muted-foreground">
                      <Icon className="size-4" stroke={1.75} aria-hidden />
                    </span>
                    {"badgeKey" in template ? (
                      <Badge variant="neutral" size="compact">
                        {t(`${translationKey}.${template.badgeKey}`)}
                      </Badge>
                    ) : (
                      <IconLock className="size-4 text-muted-foreground" stroke={1.7} aria-hidden />
                    )}
                  </span>
                  <span className="mt-3 text-[13px] font-semibold leading-5 text-ink">
                    {t(`${translationKey}.title`)}
                  </span>
                  <Badge variant="neutral" size="compact" className="mt-1.5 font-medium">
                    {t(`${translationKey}.tag`)}
                  </Badge>
                  <span className="mt-2 text-[11px] leading-4 text-muted-foreground">
                    {t(`${translationKey}.description`)}
                  </span>
                  {"noteKey" in template && (
                    <span className="mt-auto pt-2 text-[11px] text-muted-foreground">
                      {t(`${translationKey}.${template.noteKey}`)}
                    </span>
                  )}
                </button>
              );
            })}

            <div
              aria-disabled="true"
              className="flex min-h-[160px] flex-col justify-center rounded-[12px] border border-dashed border-line bg-surface p-4 opacity-55"
            >
              <p className="text-[13px] font-medium text-muted-foreground">
                {t("audiences.templates.more.title")}
              </p>
              <Button variant="outline" size="sm" disabled className="mt-3 w-fit">
                {t("audiences.templates.more.action")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-[480px] flex-1 py-5 shadow-[0_1px_3px_rgb(10_10_10_/_0.03)]">
        <CardHeader className="px-5">
          <CardTitle className="text-[18px] font-semibold tracking-[-0.02em] text-ink">
            {t("audiences.list.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 px-5">
          <Empty className="min-h-0 py-10">
            <EmptyHeader>
              <EmptyMedia variant="icon" className="mb-3 text-muted-foreground">
                <IconUsers className="size-5" aria-hidden />
              </EmptyMedia>
              <EmptyTitle className="text-[16px]">
                {t("audiences.list.unavailableTitle")}
              </EmptyTitle>
              <EmptyDescription>{t("audiences.list.unavailableDescription")}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    </main>
  );
}
